import { useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import {
  INVOICE_APPENDIX_DELETED,
  appendixErrorText,
} from 'components/invoiceDetail/invoiceAppendixText';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

// An appendix Pennylane has, or may have: deleting it in Jobbiz leaves it
// there (decision 56), so the admin is told before.
const IN_PENNYLANE_STATUSES = ['sent', 'unconfirmed'];

/**
 * Confirms the deletion of an invoice appendix (JBWEB-1277). `modalData` is
 * `{ number, appendix }`; the section drops the row on INVOICE_APPENDIX_DELETED,
 * or reads its list again when the appendix was already gone.
 */
const ModalInvoiceAppendixDelete = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceAppendices.${key}`, fallback);
  const [isDeleting, setIsDeleting] = useState(false);
  const [failure, setFailure] = useState(null);

  const number = modalData?.number;
  const appendix = modalData?.appendix;
  if (!number || !appendix) return null;
  const remainsInPennylane = IN_PENNYLANE_STATUSES.includes(appendix.status);

  const confirmDelete = () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setFailure(null);
    ApiDatabase.deleteInvoiceAppendix(
      { number, appendixId: appendix.id },
      (data) => {
        setIsDeleting(false);
        toast.success(
          data?.remainsInPennylane
            ? text(
                'deleteModal.deletedRemains',
                'Annexe supprimée de Jobbiz ; elle reste dans Pennylane.',
              )
            : text('deleteModal.deleted', 'Annexe supprimée.'),
        );
        setModalDataReturn({
          type: INVOICE_APPENDIX_DELETED,
          number,
          appendixId: appendix.id,
        });
        toggle();
      },
      (error) => {
        if (error?.name === 'CanceledError') return;
        setIsDeleting(false);
        // Never the error object in the console: axios keeps the request, token included.
        const code = error?.response?.data?.errorCode;
        setFailure(appendixErrorText(text, code, 'deleteFailed'));
        if (code === 'APPENDIX_NOT_FOUND') {
          setModalDataReturn({
            type: INVOICE_APPENDIX_DELETED,
            number,
            appendixId: appendix.id,
            reload: true,
          });
        }
      },
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isDeleting}>
        {text('deleteModal.title', 'Supprimer l’annexe')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-3 text-sm'}>
          <p className={'text-gray-700'}>
            {fillText(
              text('deleteModal.text', '« {name} » sera supprimée de Jobbiz.'),
              { name: appendix.name },
            )}
          </p>
          {remainsInPennylane && (
            <div
              role={'note'}
              className={'rounded-lg bg-yellow-50 px-4 py-3 text-yellow-800'}
            >
              {text(
                'deleteModal.remainsInPennylane',
                'Elle restera dans Pennylane : la supprimer ici ne l’en retire pas. Elle ne sera plus jointe aux mails de Jobbiz.',
              )}
            </div>
          )}
          {failure && (
            <div
              role={'alert'}
              className={'rounded-lg bg-red-50 px-4 py-3 text-red-700'}
            >
              {failure}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={isDeleting ? 'DISABLED' : 'SECONDARY'}
          disabled={isDeleting}
          onClick={toggle}
        >
          {text('deleteModal.cancel', 'Annuler')}
        </Button>
        <Button
          size={'LG'}
          color={isDeleting ? 'DISABLED' : 'PRIMARY'}
          disabled={isDeleting}
          onClick={confirmDelete}
        >
          {isDeleting
            ? text('deleteModal.deleting', 'Suppression…')
            : text('deleteModal.confirm', 'Supprimer')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalInvoiceAppendixDelete;
